import {
  Box, Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  useNavigate, useParams, useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect } from 'react';
import AppBreadCrumbs from 'src/components/AppBreadCrumb';
import UserDistributorsTable from 'src/components/users/UserDistributorsTable';
import { fetchAssignedDistributors, updateUser } from 'src/store/actions/users.action';
import AssignDistributorDialog from 'src/components/users/AssignDistributorDialog';
import { toast } from 'react-toastify';
import { IconAdFilled, IconPlus } from '@tabler/icons-react';
import HelmetSeo from '../components/HelmetSeo';

export default function UserDetails() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { state } = useLocation();
  const {
    firstName, lastName, statusId, roleId, isApproved,
  } = state;

  const name = `${firstName} ${lastName}`;
  const isSalesAgent = roleId === '4';

  const { assignedDistributorStatus, userUpdateStatus } = useSelector((st) => st.users);
  const { userStatuses, roles } = useSelector((st) => st.app);

  const [selectedUser, setSelectedUser] = useState({
    statusId,
    roleId,
    isApproved: !!isApproved,
  });

  const [assignedDistributors, setAssignedDistributors] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);

  const [selectedStatus, setSelectedStatus] = useState(userStatuses.find((status) => status.id === statusId));
  const [selectedRole, setSelectedRole] = useState(roles.find((status) => status.id === roleId));
  const [approved, setApproved] = useState(!!isApproved);

  const handleStatusChange = (event) => {
    const newStatusId = event.target.value;
    setSelectedStatus(userStatuses.find((status) => status.id === newStatusId));
    setSelectedUser({ ...selectedUser, statusId: newStatusId });
  };

  const handleRoleChange = (event) => {
    const newRoleId = event.target.value;
    setSelectedRole(roles.find((status) => status.id === newRoleId));
    setSelectedUser({ ...selectedUser, roleId: newRoleId });
  };

  const handleUserApproval = (event) => {
    const newApproved = event.target.checked;
    setApproved(newApproved);
    setSelectedUser({ ...selectedUser, isApproved: newApproved });
    console.log(newApproved);
  };

  const fetchDistributors = async () => {
    try {
      const response = await dispatch(fetchAssignedDistributors(id)).unwrap();
      setAssignedDistributors(response.data);
    } catch (err) {
      console.log(err);
    }
  };

  const updateUserDetails = async () => {
    try {
      const payload = { user: selectedUser, id };
      const response = await dispatch(updateUser(payload)).unwrap();
      if (response) {
        console.log(response);
        toast.success('User updated successfully');
      }
    } catch (err) {
      toast.error(err);
    }
  };

  const handleClickOpen = () => {
    setOpenDialog(!openDialog);
    fetchDistributors();
  };

  const onActionComplete = () => {
    fetchDistributors();
  };

  useEffect(() => {
    if (isSalesAgent) {
      fetchDistributors();
    }
  }, [dispatch, id, isSalesAgent]);

  return (
    <>
      <HelmetSeo title={`${name} | Rewot Ciro`} />

      <Card>
        <CardContent sx={{ p: 4 }}>
          <AppBreadCrumbs levelOne="Users" levelTwo={name} />
          <Divider sx={{ my: 4 }} />

          {isSalesAgent && (
            <Stack direction="row" justifyContent="end" sx={{ mb: 3 }}>
              <Button
                color="primary"
                fullWidth
                size="large"
                type="submit"
                variant="outlined"
                onClick={handleClickOpen}
                startIcon={<IconPlus />}
                sx={{
                  maxWidth: 250,
                }}
              >
                Assign Distributor
              </Button>
            </Stack>
          )}

          <Grid container spacing={4}>
            <Grid item md={6} xs={12}>
              <TextField
                select
                label="User Status"
                value={selectedStatus ? selectedStatus.id : ''}
                onChange={(e) => handleStatusChange(e)}
                sx={{ width: '100%' }}
              >
                <MenuItem disabled value="all">
                  <em>All</em>
                </MenuItem>

                {userStatuses.map((item) => (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item md={6} xs={12}>
              <TextField
                select
                label="User Role"
                value={selectedRole ? selectedRole.id : ''}
                onChange={(e) => handleRoleChange(e)}
                sx={{ width: '100%' }}
              >
                <MenuItem disabled value="all">
                  <em>All</em>
                </MenuItem>

                {roles.map((item) => (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Stack direction="row" alignContent="center" alignItems="center" sx={{ py: 2 }}>
            <Typography>Approve</Typography>
            <Checkbox
              checked={approved}
              onChange={handleUserApproval}
            />
          </Stack>

          <Stack direction="row" justifyContent="end" sx={{ my: 4 }}>
            <Button
              color="secondary"
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              disabled={userUpdateStatus === 'loading'}
              onClick={updateUserDetails}
              sx={{
                maxWidth: 250,
                minWidth: 100,
              }}
            >
              <Typography color="white">Update</Typography>
            </Button>
          </Stack>

          {isSalesAgent && (
          <Box sx={{ my: 3 }}>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h5" sx={{ mb: 2 }}>Assigned distributors</Typography>
            <UserDistributorsTable
              loading={assignedDistributorStatus === 'loading'}
              users={assignedDistributors}
              onFinished={onActionComplete}
            />
          </Box>
          )}
        </CardContent>
      </Card>

      <AssignDistributorDialog open={openDialog} setOpen={handleClickOpen} />
    </>
  );
}
